
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { RefreshCw, Trash2, Settings, Users, ShoppingCart, Tag } from 'lucide-react';
import { Integration, NuvemshopConfig } from '@/types/integrations';
import { NuvemshopConnectionHandlers, NuvemshopSyncHandlers, NuvemshopConfigHandlers } from '../types';

interface NuvemshopActiveStateProps extends NuvemshopConnectionHandlers, NuvemshopSyncHandlers, NuvemshopConfigHandlers {
  integration: Integration;
}

export const NuvemshopActiveState = ({ 
  integration,
  handleDisconnect,
  handleSyncCustomers,
  handleSyncOrders,
  isSyncing,
  updateSyncSetting,
  isConfiguring,
  setIsConfiguring
}: NuvemshopActiveStateProps) => {
  const config = integration.config as NuvemshopConfig;

  console.log('🎯 NuvemshopActiveState: Renderizando estado ativo', {
    storeId: config.store_id,
    storeName: config.store_name,
    status: integration.status,
    connectionStatus: integration.connection_status
  });

  // Helper function to safely get store name
  const getStoreName = () => {
    if (typeof config.store_name === 'string') {
      return config.store_name;
    }
    if (typeof config.store_name === 'object' && config.store_name !== null) {
      return (config.store_name as any).pt || config.store_name;
    }
    return `Loja ${config.store_id}`;
  };

  return (
    <div className="space-y-6">
      {/* Informações da Loja */}
      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <h4 className="font-medium text-green-800">
                {getStoreName()}
              </h4>
              <p className="text-sm text-green-600">
                ID da Loja: {config.store_id} • Conectado com sucesso
              </p>
            </div>
          </div>
          <Badge variant="default" className="bg-green-100 text-green-800">
            Ativo
          </Badge>
        </div>
      </div>

      {/* Configurações de Sincronização */}
      {isConfiguring && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configurações de Sincronização
            </CardTitle>
            <CardDescription>
              Configure quais dados serão sincronizados automaticamente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Sincronizar Clientes</span>
              </div>
              <Switch 
                checked={config.sync_customers || false}
                onCheckedChange={(checked) => updateSyncSetting('sync_customers', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                <span>Sincronizar Pedidos</span>
              </div>
              <Switch 
                checked={config.sync_orders || false}
                onCheckedChange={(checked) => updateSyncSetting('sync_orders', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                <span>Criação Automática de Cashback</span>
              </div>
              <Switch 
                checked={config.auto_create_coupons || false}
                onCheckedChange={(checked) => updateSyncSetting('auto_create_coupons', checked)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ações de Sincronização */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button 
          onClick={handleSyncCustomers}
          disabled={isSyncing}
          variant="outline"
          className="w-full"
        >
          <Users className="h-4 w-4 mr-2" />
          {isSyncing ? 'Sincronizando...' : 'Sincronizar Clientes'}
        </Button>
        
        <Button 
          onClick={handleSyncOrders}
          disabled={isSyncing}
          variant="outline"
          className="w-full"
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          {isSyncing ? 'Sincronizando...' : 'Sincronizar Pedidos'}
        </Button>
      </div>

      {/* Informações Técnicas */}
      <div className="text-xs text-muted-foreground p-3 bg-gray-50 rounded border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div>
            <strong>Última Sincronização:</strong> {' '}
            {integration.last_sync_at 
              ? new Date(integration.last_sync_at).toLocaleString('pt-BR')
              : 'Nunca'
            }
          </div>
          <div>
            <strong>Token:</strong> {' '}
            {config.access_token ? '••••••••' + config.access_token.slice(-8) : 'N/A'}
          </div>
        </div>
      </div>

      {/* Ações de Gerenciamento */}
      <div className="flex gap-2 pt-4 border-t">
        <Button 
          variant="outline" 
          onClick={() => setIsConfiguring(!isConfiguring)}
          className="flex-1"
        >
          <Settings className="h-4 w-4 mr-2" />
          {isConfiguring ? 'Fechar Config.' : 'Configurações'}
        </Button>
        
        <Button 
          variant="destructive"
          onClick={handleDisconnect}
          size="sm"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Desconectar
        </Button>
      </div>
    </div>
  );
};
